import useClearCalendarFeedToken from '@/api/mutations/user/useClearCalendarFeedToken';
import useGenerateCalendarFeedToken from '@/api/mutations/user/useGenerateCalendarFeedToken';
import useRegenerateCalendarFeedToken from '@/api/mutations/user/useRegenerateCalendarFeedToken';
import useGetMyCalendarFeed from '@/api/queries/me/useGetMyCalendarFeed';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import {
  Alert,
  AnchorButton,
  Button,
  Card,
  Input,
  Modal,
  SkeletonLoader,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';

type PendingAction = 'regenerate' | 'clear';

const CONFIRM_COPY: Record<
  PendingAction,
  { title: string; body: string; confirmLabel: string }
> = {
  regenerate: {
    title: 'Regenerate calendar link',
    body: 'Regenerating immediately stops the old link from working. Anyone already subscribed will need the new link.',
    confirmLabel: 'Regenerate',
  },
  clear: {
    title: 'Remove calendar link',
    body: 'Removing stops the feed immediately. Anyone subscribed will stop receiving updates until you generate a new link.',
    confirmLabel: 'Remove',
  },
};

const PersonalCalendarSubscription: FC = () => {
  const { calendarFeed, isPending } = useGetMyCalendarFeed();
  const { handleCopy } = useCopyToClipboard();
  const generate = useGenerateCalendarFeedToken();
  const regenerate = useRegenerateCalendarFeedToken();
  const clear = useClearCalendarFeedToken();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  const confirm = pendingAction ? CONFIRM_COPY[pendingAction] : null;

  const runPendingAction = () => {
    const mutation = pendingAction === 'clear' ? clear : regenerate;
    mutation.mutate(undefined, { onSuccess: () => setPendingAction(null) });
  };

  return (
    <div className="flex flex-col gap-y-4 w-full max-w-screen-xl m-auto">
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Calendar subscription</h2>
          <p className="mb-4">
            Subscribe to a private feed in Google Calendar, Apple Calendar or
            Outlook to see every scheduled event assigned to you. It is
            read-only. Keep the link secret — anyone with it can see your
            schedule.
          </p>

          {isPending && <SkeletonLoader className="h-10 w-full" />}

          {!isPending && !calendarFeed && (
            <div>
              <Button
                isProcessing={generate.isPending}
                onClick={() => generate.mutate()}
              >
                Generate calendar link
              </Button>
            </div>
          )}

          {!isPending && calendarFeed && (
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  readOnly
                  value={calendarFeed.url}
                  aria-label="Calendar feed URL"
                  className="grow"
                  onFocus={(event) => event.target.select()}
                />
                <Button
                  variant="secondary"
                  onClick={() => handleCopy(calendarFeed.url)}
                >
                  <span className="flex items-center gap-x-2">
                    <UntitledIcon name="copy-07.3" className="h-4 w-4" />
                    Copy
                  </span>
                </Button>
              </div>

              <div className="flex flex-wrap gap-3">
                <AnchorButton href={calendarFeed.webcalUrl}>
                  Subscribe in calendar app
                </AnchorButton>
                <Button
                  variant="secondary"
                  onClick={() => setPendingAction('regenerate')}
                >
                  Regenerate link
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setPendingAction('clear')}
                >
                  Remove link
                </Button>
              </div>

              <p className="supporting-body">
                Calendar apps refresh subscriptions on their own schedule
                (Google every 8–24 hours, Outlook every 3–24 hours, Apple as
                often as every 5 minutes). Changes in Payaca may take a while to
                appear.
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        title={confirm?.title ?? ''}
      >
        <Modal.Body>
          <Alert iconName="alert-triangle.3" colour="yellow">
            {confirm?.body}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" onClick={() => setPendingAction(null)}>
              Cancel
            </Button>
            <Button
              isProcessing={regenerate.isPending || clear.isPending}
              onClick={runPendingAction}
            >
              {confirm?.confirmLabel ?? 'Confirm'}
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonalCalendarSubscription;
